import React, { useState, useEffect, useMemo } from "react";
import { S, CONTACT_ROLES } from "../../crm/theme";
import { fetchLeads, fetchContacts, createContact, updateContact, deleteContact, visibleTo } from "../../crm/crmApi";
import ContactModal from "../../crm/ContactModal";

export default function CrmContactsScreen({ user }) {
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [modalContact, setModalContact] = useState(undefined);
  const [filterRole, setFilterRole] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [allLeads, allContacts] = await Promise.all([fetchLeads(), fetchContacts()]);
      const myLeads = visibleTo(allLeads, user);
      setLeads(myLeads);
      setContacts(visibleTo(allContacts, user));
    } catch (e) {
      setMsg("Error loading contacts: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const leadName = (leadId) => leads.find((l) => l.id === leadId)?.societyName || "—";

  const filtered = useMemo(
    () => (filterRole ? contacts.filter((c) => c.role === filterRole) : contacts),
    [contacts, filterRole]
  );

  const handleSave = async (data) => {
    if (modalContact && modalContact.id) {
      await updateContact(modalContact.id, data);
      setMsg("Contact updated.");
    } else {
      await createContact(data, user);
      setMsg("Contact added.");
    }
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await deleteContact(id);
    setModalContact(undefined);
    setMsg("Contact deleted.");
    await load();
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>👤 Contacts</div>
        <button style={S.btnBlue} onClick={() => setModalContact(null)} disabled={leads.length === 0}>+ New Contact</button>
      </div>

      {leads.length === 0 && !loading && (
        <div style={S.msg}>Add a lead in the Pipeline first — every contact needs to be linked to one.</div>
      )}
      {msg && <div style={S.msg}>{msg}</div>}

      <div style={{ marginBottom: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#7b82a0" }}>Filter by role:</span>
        <select style={{ ...S.select, width: "auto" }} value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
          <option value="">All</option>
          {CONTACT_ROLES.map((r) => <option key={r}>{r}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ color: "#7b82a0" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ color: "#7b82a0" }}>No contacts yet.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>Name</th><th style={S.th}>Role</th><th style={S.th}>Society</th>
                <th style={S.th}>Phone</th><th style={S.th}>Email</th><th style={S.th}>Last Interaction</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const last = (c.interactions || [])[c.interactions?.length - 1];
                return (
                  <tr key={c.id} onClick={() => setModalContact(c)} style={{ cursor: "pointer" }}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{c.name}</td>
                    <td style={S.td}>{c.role}</td>
                    <td style={S.td}>{leadName(c.leadId)}</td>
                    <td style={S.td}>{c.phone || "—"}</td>
                    <td style={S.td}>{c.email || "—"}</td>
                    <td style={{ ...S.td, color: "#7b82a0" }}>{last ? last.note : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalContact !== undefined && (
        <ContactModal
          contact={modalContact}
          leads={leads}
          user={user}
          onSave={handleSave}
          onClose={() => setModalContact(undefined)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
